import { useEffect, useState, FC, ReactNode } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Grid,
  Tooltip,
  InputAdornment
} from "@mui/material";
import {
  Groups,
  Person,
  Business,
  ContactPhone,
  SwapHoriz,
  Inventory,
  Description,
  Repeat,
  Language,
  BarChart,
  Comment,
  Gavel,
  Bolt,
  Storefront
} from "@mui/icons-material";
import { MFT_ENERGY_AS, mftCompanies } from "../../../utils/consts";
import { CreateBrokerRequest, VpDto } from "../../../api/Api";
import { VpPicker } from "../../../components/vpPicker";
import { UserNamePicker } from "../../../components/userPicker";
import { buildCommentDto } from "../../../utils/commentUtils";
import { US, DK, JP, TR, EU, GB } from "country-flag-icons/react/3x2";
import { useThemeMode } from "../../../theme";

type Props = {
  initialValues?: CreateBrokerRequest | null;
  onSubmit: (payload: CreateBrokerRequest) => void;
  submitting?: boolean;
  submitDisabled?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
  cancelLabel?: string;
  secondaryAction?: ReactNode;
  /** Full-width slot above the buttons, see CounterpartyForm. */
  extraSection?: ReactNode;
  /** Hides "Other info or comments", which only makes sense when creating. */
  hideComment?: boolean;
};

export const BrokerForm: FC<Props> = ({
  initialValues,
  onSubmit,
  submitting = false,
  submitDisabled: submitDisabledProp = false,
  submitLabel = "Submit",
  onCancel,
  cancelLabel = "Cancel",
  secondaryAction,
  extraSection,
  hideComment = false
}) => {
  const [tradingTeam, setTradingTeam] = useState<string>("");
  const [contactPersonTradingTeam, setContactPersonTradingTeam] =
    useState<string>("");
  const [brokerName, setBrokerName] = useState<string>("");
  const [brokerContactName, setBrokerContactName] = useState<string>("");
  const [brokerContactEmail, setBrokerContactEmail] = useState<string>("");
  const [typeOfTrading, setTypeOfTrading] = useState<string>("");
  const [productType, setProductType] = useState<string>("");
  const [venue, setVenue] = useState<string>("");
  const [agreementType, setAgreementType] = useState<string>("");
  const [transactionType, setTransactionType] = useState<string>("");
  const [unit, setUnit] = useState<string>("");
  const [volumeSize, setVolumeSize] = useState<number | undefined>(undefined);
  const [urgent, setUrgent] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [contractingEntity, setContractingEntity] = useState<string>("");
  const [vp, setVp] = useState<VpDto | null>(null);

  const { mode } = useThemeMode();

  useEffect(() => {
    setTradingTeam(initialValues?.tradingTeam ?? "");
    setContactPersonTradingTeam(initialValues?.contactPersonTradingTeam ?? "");
    setBrokerName(initialValues?.brokerName ?? "");
    setBrokerContactName(initialValues?.brokerContactName ?? "");
    setBrokerContactEmail(initialValues?.brokerContactEmail ?? "");
    setTypeOfTrading(initialValues?.typeOfTrading ?? "");
    setProductType(initialValues?.productType ?? "");
    setVenue(initialValues?.venue ?? "");
    setAgreementType(initialValues?.agreementType ?? "");
    setTransactionType(initialValues?.transactionType ?? "");
    setUnit(initialValues?.unit ?? "");
    setVolumeSize(initialValues?.volumeSize);
    setUrgent(initialValues?.urgent ?? false);
    setComment(initialValues?.comment?.comment ?? "");
    setContractingEntity(initialValues?.contractingEntity ?? "");
    setVp(initialValues?.vp ?? null);
  }, [initialValues]);

  const buildFormData = (): CreateBrokerRequest => {
    const trimmedComment = comment.trim();
    const initialComment = initialValues?.comment;
    const initialCommentText = initialComment?.comment?.trim();

    return {
      tradingTeam: tradingTeam.trim() || undefined,
      contactPersonTradingTeam: contactPersonTradingTeam.trim() || undefined,
      brokerName: brokerName.trim() || undefined,
      brokerContactName: brokerContactName.trim() || undefined,
      brokerContactEmail: brokerContactEmail.trim() || undefined,
      typeOfTrading: typeOfTrading.trim() || undefined,
      productType: productType.trim() || undefined,
      venue: venue.trim() || undefined,
      agreementType: agreementType.trim() || undefined,
      transactionType: transactionType.trim() || undefined,
      unit: unit.trim() || undefined,
      volumeSize: volumeSize,
      contractingEntity: contractingEntity.trim() || MFT_ENERGY_AS,
      vp: vp ?? undefined,
      urgent,
      comment: trimmedComment.length
        ? initialComment && trimmedComment === initialCommentText
          ? initialComment
          : buildCommentDto(trimmedComment)
        : undefined
    };
  };

  const submitDisabled =
    !tradingTeam ||
    !contactPersonTradingTeam ||
    !brokerName ||
    !brokerContactName ||
    !brokerContactEmail ||
    !typeOfTrading ||
    !productType ||
    !venue.trim() ||
    !agreementType ||
    !transactionType ||
    !vp?.objectId;

  return (
    <Grid container spacing={2} sx={{ paddingTop: 2 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FormControl fullWidth required>
            <InputLabel>Trading team</InputLabel>
            <Select
              value={tradingTeam}
              onChange={(e) => setTradingTeam(e.target.value)}
              label="Trading team"
              startAdornment={
                <InputAdornment position="start">
                  <Groups fontSize="small" />
                </InputAdornment>
              }
            >
              {mftCompanies.map((company) => {
                const split = company.split("MFT");
                return (
                  <MenuItem key={company} value={company}>
                    {"MFT " + split[1]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            label="Contracting Entity (if applicable)"
            value={contractingEntity}
            placeholder="Defaults to MFT Energy A/S"
            onChange={(e) => setContractingEntity(e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Gavel fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <UserNamePicker
            label="Contact person from MFT trading team"
            value={contactPersonTradingTeam}
            onChange={setContactPersonTradingTeam}
            required
            startIcon={<Person fontSize="small" />}
          />

          <VpPicker value={vp} onChange={setVp} required />

          <TextField
            label="Broker name"
            value={brokerName}
            onChange={(e) => setBrokerName(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Business fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <TextField
            label="Broker contact name"
            value={brokerContactName}
            onChange={(e) => setBrokerContactName(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ContactPhone fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <TextField
            label="Broker contact email"
            value={brokerContactEmail}
            onChange={(e) => setBrokerContactEmail(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ContactPhone fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <TextField
            label="Type of trading/settlement"
            value={typeOfTrading}
            onChange={(e) => setTypeOfTrading(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SwapHoriz fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <TextField
            label="Product type"
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Inventory fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <TextField
            label="Venue"
            placeholder="EEX, Trayport, ICE etc."
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Storefront fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TextField
            label="Agreement type"
            value={agreementType}
            onChange={(e) => setAgreementType(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Description fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <FormControl fullWidth required>
            <InputLabel>Transaction type</InputLabel>
            <Select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              label="Transaction type"
              startAdornment={
                <InputAdornment position="start">
                  <Repeat fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="one-off">One-off Transaction</MenuItem>
              <MenuItem value="recurring">Recurring Transactions</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Deal Unit</InputLabel>
            <Select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              label="Deal Unit"
              startAdornment={
                <InputAdornment position="start">
                  <Language fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="DKK">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="64px"
                >
                  <Typography variant="body1">DKK</Typography>
                  <DK
                    height={12}
                    title="DKK"
                    style={{
                      border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem value="EUR">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="64px"
                >
                  <Typography variant="body1">EUR</Typography>
                  <EU
                    height={12}
                    title="EUR"
                    style={{
                      border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem value="GBP">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="64px"
                >
                  <Typography variant="body1">GBP</Typography>
                  <GB
                    height={12}
                    title="GBP"
                    style={{
                      border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem value="JPY">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="64px"
                >
                  <Typography variant="body1">JPY</Typography>
                  <JP
                    height={12}
                    title="JPY"
                    style={{
                      border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem value="TRY">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="64px"
                >
                  <Typography variant="body1">TRY</Typography>
                  <TR
                    height={12}
                    title="TRY"
                    style={{
                      border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem value="USD">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="64px"
                >
                  <Typography variant="body1">USD</Typography>
                  <US
                    height={12}
                    title="USD"
                    style={{
                      border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem value="MW">
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="64px"
                >
                  <Typography variant="body1">MW</Typography>
                  <Bolt height={12} color="warning" />
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Expected deal volume/size"
            type="number"
            value={volumeSize || ""}
            onChange={(e) =>
              setVolumeSize(e.target.value ? Number(e.target.value) : undefined)
            }
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <BarChart fontSize="small" />
                  </InputAdornment>
                )
              }
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={urgent}
                onChange={(e) => setUrgent(e.target.checked)}
              />
            }
            label="Mark as urgent"
          />

          {!hideComment && (
            <TextField
              label="Other info or comments"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              rows={2.8}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Comment fontSize="small" />
                    </InputAdornment>
                  )
                }
              }}
            />
          )}
        </Box>
      </Grid>

      {extraSection && <Grid size={{ xs: 12 }}>{extraSection}</Grid>}

      <Grid size={{ xs: 12 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            paddingTop: 2
          }}
        >
          {onCancel && (
            <Button variant="text" onClick={onCancel} disabled={submitting}>
              {cancelLabel}
            </Button>
          )}
          {secondaryAction}
          <Tooltip title="* fields are required" arrow placement="top">
            <span>
              <Button
                loading={submitting}
                variant="contained"
                disabled={submitDisabled || submitDisabledProp || submitting}
                onClick={() => onSubmit(buildFormData())}
              >
                {submitLabel}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Grid>
    </Grid>
  );
};
